/**
 * Chilean RUT validation (dígito verificador algorithm).
 *
 * A RUT is written `XXXXXXXX-X` where the trailing character is the DV
 * (0-9 or K). The DV is computed by multiplying the RUT body digits
 * right-to-left by the cyclic sequence 2,3,4,5,6,7, summing, taking mod 11,
 * and mapping: 11 → 0, 10 → K, otherwise the remainder subtracted from 11.
 */

/** Normalize "12.345.678-5" / "12345678-5" → body string + uppercase DV. */
export function parseRut(input: string): { body: string; dv: string } | null {
  if (!input) return null;
  const cleaned = input.replace(/[.\s-]/g, "").toUpperCase();
  if (cleaned.length < 2) return null;
  const dv = cleaned.slice(-1);
  const body = cleaned.slice(0, -1);
  if (!/^\d+$/.test(body)) return null;
  if (!/^[0-9K]$/.test(dv)) return null;
  return { body, dv };
}

/** Compute the expected DV for a RUT body (digits only). */
export function computeDv(body: string): string {
  let sum = 0;
  let factor = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const remainder = sum % 11;
  const dv = 11 - remainder;
  if (dv === 11) return "0";
  if (dv === 10) return "K";
  return String(dv);
}

/** True when the RUT (any common format) has a valid dígito verificador. */
export function validarRut(input: string): boolean {
  const parsed = parseRut(input);
  if (!parsed) return false;
  return computeDv(parsed.body) === parsed.dv;
}

/** Normalize a RUT to the canonical `XXXXXXXX-X` form (no thousands dots). */
export function normalizeRut(input: string): string {
  const parsed = parseRut(input);
  if (!parsed) return input;
  return `${parsed.body}-${parsed.dv}`;
}

/** Format a RUT to `XX.XXX.XXX-X` display form with thousands dots. Accepts any input. */
export function formatRut(input: string): string {
  const parsed = parseRut(input);
  if (!parsed) return input;
  const body = parsed.body.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${body}-${parsed.dv}`;
}
